/* eslint-disable indent */
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React, { useRef } from 'react';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import InputAdornmentChip from 'components/InputAdornmentChip';
import { Formik } from 'formik';
import GenericForm from 'skyone-lib/lib/GenericForm';
import i18n from 'i18n';
import Button from 'components/Button';
import {
  EnvironmentFields,
  PublicationResponse,
  PublishImageProps,
  ServerFields,
} from 'modules/environments/interfaces';
import {
  getPublishHistoryRequest,
  publishImageRequest,
} from 'modules/environments/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import PageTitle from 'components/PageTitle';
import clsx from 'clsx';
import { publishImagePartnerRequest } from 'modules/partners/actions';
import PublishHistory from 'components/PublishHistory';
import validationSchema from './schema';
import styles from './styles';
import PublishExamples from './PublishExamples';

const useStyles = makeStyles(styles);

interface Props {
  environmentSelected: EnvironmentFields | null;
  preparationServer?: ServerFields;
  highlightSection?: string;
  instanceIds?: string[];
  modalVersion?: boolean;
  partnerUuid?: string;
  publishHistory: PublicationResponse;
  isLoading: boolean;
  inlineAction?: boolean;
  environmentUuid?: string;
  publishImageRequest: ({ environmentUuid, instanceId, values }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  getPublishHistoryRequest: ({ environmentUuid }) => void;
  publishImagePartnerRequest: ({
    partnerUuid,
    values,
    environmentUuid,
    inlineAction,
  }) => void;
  setCurrentTab?: React.Dispatch<React.SetStateAction<number>>;
  handleOptimizedPublish?: (ids: string[]) => void;
  reopenModal?: () => void;
}

const OptimizedPublication = ({
  environmentSelected,
  preparationServer,
  highlightSection,
  instanceIds,
  modalVersion,
  partnerUuid,
  publishHistory,
  isLoading,
  inlineAction,
  environmentUuid,
  publishImageRequest,
  modalOpen,
  modalClose,
  getPublishHistoryRequest,
  publishImagePartnerRequest,
  setCurrentTab,
  handleOptimizedPublish,
  reopenModal,
}: Props) => {
  const classes = useStyles();

  const sectionRef = useRef<HTMLDivElement>(null);

  const isPublicationRunning =
    publishHistory?.publications?.[0]?.status === 'RUNNING';

  React.useEffect(() => {
    if (isPublicationRunning && modalVersion) {
      const runningStep = publishHistory?.publications[0].steps.find(
        step => step.status === 'RUNNING'
      );

      const correspondingTemplateStep = publishHistory?.publication_template_steps.find(
        templateStep => templateStep?.name === runningStep?.name
      );

      const intervalDuration = Math.min(
        correspondingTemplateStep?.duration_milliseconds || 100,
        5000
      );

      const intervalId = setInterval(() => {
        getPublishHistoryRequest({
          environmentUuid,
        });
      }, intervalDuration);

      return () => clearInterval(intervalId);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishHistory, modalVersion, environmentUuid]);

  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  if (highlightSection === 'logs' || isPublicationRunning) {
    scrollToSection();
  }

  const initialValues: PublishImageProps = {
    stop_server: false,
    notify_list: environmentSelected?.trusted_users || '',
    replace_instances: {
      block_instances: false,
      block_users: false,
      block_users_message: '',
      block_users_seconds: null,
    },
  };
  const initialValuesPartner: PublishImageProps = {
    instance_ids: instanceIds,
    stop_server: false,
    notify_list: environmentSelected?.trusted_users || '',
    replace_instances: {
      block_instances: false,
      block_users: false,
      block_users_message: '',
      block_users_seconds: null,
    },
  };

  const handleHowToOptimize = () => {
    modalOpen({
      title: i18n.t('HOW_TO_OPTIMIZE_KEY'),
      maxWidth: 'md',
      footer: (
        <Button
          id="cancel-button"
          variant="outlined"
          onClick={() => {
            if (handleOptimizedPublish && instanceIds) {
              handleOptimizedPublish(instanceIds);
            } else {
              modalClose();
            }
          }}
        >
          {modalVersion ? i18n.t('BACK_KEY') : i18n.t('CLOSE_KEY')}
        </Button>
      ),
      content: <PublishExamples />,
    });
  };

  const handleConfirmPublish = values => {
    modalOpen({
      title: i18n.t('OPTIMIZED_PUBLICATION_KEY'),
      maxWidth: 'sm',
      footer: (
        <Grid>
          <Button
            id="submit-button"
            submitButton
            onClick={() => {
              if (modalVersion) {
                publishImagePartnerRequest({
                  partnerUuid,
                  values,
                  environmentUuid,
                  inlineAction,
                });
                modalClose();
              }
              if (reopenModal && inlineAction && modalVersion) {
                reopenModal();
              }
              if (!modalVersion) {
                publishImageRequest({
                  environmentUuid: environmentSelected?.environment_uuid,
                  instanceId: preparationServer?.instance_id,
                  values,
                });
                modalClose();
              }
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
      content: (
        <Typography className={classes.headerText}>
          {i18n.t('OPTIMIZED_PUBLICATION_CONFIRM_KEY')}
        </Typography>
      ),
    });
  };

  return (
    <>
      <Grid
        container
        className={clsx(classes.formGrid, {
          [classes.formGridModal]: !!modalVersion,
        })}
      >
        {isPublicationRunning || isLoading ? (
          <>
            <Grid container justify="center" alignItems="center">
              <CircularProgress size={15} />

              {isPublicationRunning && (
                <Typography className={classes.inProgress}>
                  {i18n.t('PUBLICATION_IN_PROGRESS_KEY')}
                </Typography>
              )}
            </Grid>
            {modalVersion && (
              <Grid container justify="flex-end">
                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={modalClose}
                  className={classes.closeModalGrid}
                >
                  {i18n.t('CLOSE_KEY')}
                </Button>
              </Grid>
            )}
          </>
        ) : (
          <Formik
            initialValues={modalVersion ? initialValuesPartner : initialValues}
            validationSchema={validationSchema}
            onSubmit={handleConfirmPublish}
            enableReinitialize
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid container spacing={2} alignItems="center">
                <InputAdornmentChip
                  gridXs={12}
                  label={i18n.t('EMAILS_TO_NOTIFY_KEY')}
                  stateKey="notify_list"
                  values={values?.notify_list?.split(',') || []}
                  setFieldValue={setFieldValue}
                />
                <Grid item xs={3} className={classes.optimizeChecks}>
                  <GenericForm
                    fields={values}
                    screenFields={[
                      {
                        gridXs: 9,
                        label: i18n.t('BLOCK_INSTANCES_KEY'),
                        stateKey: 'replace_instances.block_instances',
                        isCheckbox: true,
                      },
                    ]}
                    setFieldValue={(key, value) => {
                      if (
                        key === 'replace_instances.block_instances' &&
                        value === false
                      ) {
                        setFieldValue('replace_instances.block_users', false);
                      }
                      setFieldValue(key, value);
                    }}
                    errors={errors}
                  />
                  <Tooltip arrow title={i18n.t('BLOCK_INSTANCES_TOOLTIP_KEY')}>
                    <HelpOutlineIcon className={classes.helpIconForm} />
                  </Tooltip>
                </Grid>
                <Grid container item xs={3} className={classes.optimizeChecks}>
                  <GenericForm
                    fields={values}
                    screenFields={[
                      {
                        gridXs: 9,
                        label: i18n.t('BLOCK_USERS_KEY'),
                        stateKey: 'replace_instances.block_users',
                        isCheckbox: true,
                      },
                    ]}
                    setFieldValue={(key, value) => {
                      if (key === 'replace_instances.block_users') {
                        setFieldValue(
                          'replace_instances.block_instances',
                          true
                        );
                      }
                      setFieldValue(key, value);
                    }}
                    errors={errors}
                  />
                  <Tooltip arrow title={i18n.t('BLOCK_USERS_TOOLTIP_KEY')}>
                    <HelpOutlineIcon className={classes.helpIconForm} />
                  </Tooltip>
                </Grid>
                <Grid container item xs={6} className={classes.optimizeChecks}>
                  <GenericForm
                    fields={values}
                    screenFields={[
                      {
                        gridXs: 9,
                        label: i18n.t('STOP_SERVER_KEY'),
                        stateKey: 'stop_server',
                        isCheckbox: true,
                      },
                    ]}
                    setFieldValue={setFieldValue}
                    errors={errors}
                  />
                  <Tooltip
                    arrow
                    title={i18n.t('HOW_TO_OPTIMIZE_STOP_SERVER_KEY')}
                  >
                    <HelpOutlineIcon className={classes.helpIconFormServer} />
                  </Tooltip>
                </Grid>
                {values?.replace_instances?.block_instances &&
                  !values?.replace_instances?.block_users &&
                  !modalVersion && (
                    <Grid
                      container
                      justify="flex-start"
                      spacing={2}
                      className={classes.executedPublicationsModal}
                    >
                      <Grid
                        item
                        xs={6}
                        className={classes.executedPublications}
                      >
                        <TextField
                          id="executed-instance-blocking-publications"
                          label={i18n.t(
                            'ENVIRONMENTS_PAGE.EXECUTED_MONTHLY_PUBLICATION_KEY'
                          )}
                          value={
                            environmentSelected &&
                            environmentSelected?.publication
                              ?.limit_instance_blocking_publications -
                              environmentSelected?.publication
                                ?.executed_instance_blocking_publications
                          }
                          variant="outlined"
                          type="number"
                          InputProps={{ readOnly: true }}
                          disabled={true}
                          helperText={
                            <Typography
                              component="div"
                              className={classes.limitText}
                            >
                              {`${i18n.t('MAX_PUBLICATIONS_KEY')}: `}
                              <Box
                                fontWeight="bold"
                                display="inline"
                                className={classes.limitTextBold}
                              >
                                {
                                  environmentSelected?.publication
                                    ?.limit_instance_blocking_publications
                                }
                              </Box>
                            </Typography>
                          }
                        />
                      </Grid>
                      {environmentSelected?.publication
                        ?.limit_instance_blocking_publications_daily !==
                        null && (
                        <Grid
                          item
                          xs={6}
                          className={classes.executedPublications}
                        >
                          <TextField
                            id="executed-instance-blocking-publications-daily"
                            label={i18n.t(
                              'ENVIRONMENTS_PAGE.EXECUTED_DAILY_PUBLICATION_KEY'
                            )}
                            value={
                              environmentSelected &&
                              Number(
                                environmentSelected?.publication
                                  ?.limit_instance_blocking_publications_daily
                              ) -
                                environmentSelected?.publication
                                  ?.executed_instance_blocking_publications_daily
                            }
                            variant="outlined"
                            type="number"
                            InputProps={{ readOnly: true }}
                            disabled={true}
                            helperText={
                              <Typography
                                component="div"
                                className={classes.limitText}
                              >
                                {`${i18n.t('MAX_DAILY_PUBLICATIONS_KEY')}: `}
                                <Box
                                  fontWeight="bold"
                                  display="inline"
                                  className={classes.limitTextBold}
                                >
                                  {
                                    environmentSelected?.publication
                                      ?.limit_instance_blocking_publications_daily
                                  }
                                </Box>
                              </Typography>
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                {values?.replace_instances?.block_users && (
                  <Grid container spacing={2} className={classes.fieldsToFill}>
                    <GenericForm
                      fields={values}
                      screenFields={[
                        {
                          gridXs: 6,
                          label: i18n.t(
                            'ENVIRONMENTS_PAGE.TIME_DISCONNECT_KEY'
                          ),
                          stateKey: 'replace_instances.block_users_seconds',
                          isSelect: true,
                          options: [
                            { label: `5 ${i18n.t('MINUTES_KEY')}`, value: 300 },
                            {
                              label: `10 ${i18n.t('MINUTES_KEY')}`,
                              value: 600,
                            },
                            {
                              label: `15 ${i18n.t('MINUTES_KEY')}`,
                              value: 900,
                            },
                            {
                              label: `20 ${i18n.t('MINUTES_KEY')}`,
                              value: 1200,
                            },
                            {
                              label: `25 ${i18n.t('MINUTES_KEY')}`,
                              value: 1500,
                            },
                            {
                              label: `30 ${i18n.t('MINUTES_KEY')}`,
                              value: 1800,
                            },
                          ],
                        },
                      ]}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                    <GenericForm
                      fields={values}
                      screenFields={[
                        {
                          gridXs: 6,
                          label: i18n.t('ENVIRONMENTS_PAGE.USER_MSG_IMAGE_KEY'),
                          stateKey: 'replace_instances.block_users_message',
                        },
                      ]}
                      setFieldValue={setFieldValue}
                      errors={errors}
                    />
                  </Grid>
                )}

                <Grid
                  container
                  justify="space-between"
                  className={clsx({
                    [classes.buttonsModal]: !!modalVersion,
                  })}
                >
                  <Grid item>
                    <Button
                      className={classes.howToOptimize}
                      startIcon={
                        <HelpOutlineIcon className={classes.helpIcon} />
                      }
                      onClick={handleHowToOptimize}
                    >
                      {i18n.t('HOW_TO_OPTIMIZE_KEY')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      id="submit-button"
                      onClick={submitForm}
                      submitButton
                    >
                      {i18n.t('ENVIRONMENTS_PAGE.PUBLICATIONS_KEY')}
                    </Button>
                    {modalVersion && (
                      <Button
                        id="close-button"
                        variant="outlined"
                        onClick={modalClose}
                      >
                        {i18n.t('CLOSE_KEY')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Formik>
        )}
      </Grid>
      {!modalVersion && (
        <>
          <Grid container>
            <Divider className={classes.divider} />
          </Grid>
          <Grid container>
            <Grid container className={classes.actionsGrid}>
              <Grid item>
                <PageTitle
                  title={i18n.t('HISTORY_LOGS_PUBLISH_OPTIMIZED_KEY')}
                  subtitle={i18n.t(
                    'HISTORY_LOGS_PUBLISH_OPTIMIZED_DESCRIPTION_KEY'
                  )}
                />
              </Grid>
              <Grid item>
                {setCurrentTab && (
                  <Permission value="list_models">
                    <Button
                      className={classes.seeModelsButtons}
                      variant="outlined"
                      onClick={() => setCurrentTab(2)}
                    >
                      {i18n.t('SEE_GENERATED_MODELS_KEY')}
                    </Button>
                  </Permission>
                )}
              </Grid>
            </Grid>
            <Grid container innerRef={sectionRef}>
              <PublishHistory
                setCurrentTab={setCurrentTab}
                requestInfos={() =>
                  getPublishHistoryRequest({
                    environmentUuid: environmentSelected?.environment_uuid,
                  })
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, publishHistory, isLoading },
}: ApplicationState) => ({
  environmentSelected,
  isLoading,
  publishHistory,
});

export default connect(mapStateToProps, {
  publishImageRequest,
  publishImagePartnerRequest,
  getPublishHistoryRequest,
  modalOpen,
  modalClose,
})(OptimizedPublication);
